import type { IAdViewManaged } from "@finn-no/adview-provider-frontend-types";
import { i18n } from "@lingui/core";
import { WIcon } from "@mobility-item-page/client";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import { useEffect, useRef } from "react";
import { BrandLogo } from "../../assets/BrandLogo.tsx";
import { useAdViewAd } from "../../context/AdViewProviderContext.tsx";
import { useAppConfig } from "../../context/AppConfigProviderContext.tsx";
import { getReview } from "../../utils/expertReviewMapper.ts";
import usePulse from "../../utils/pulse-tracking/usePulse.ts";

interface ExpertReviewCardProps {
  carModelTaxonomy: IAdViewManaged;
  year: number;
}

export const ExpertReviewCard = ({ carModelTaxonomy, year }: ExpertReviewCardProps) => {
  const { brand } = useAppConfig();
  const { adViewType } = useAdViewAd();
  const { trackClick, trackElementView } = usePulse();
  const cardRef = useRef<HTMLDivElement>(null);

  const isValidAdViewType = ["car-new", "car-leasing", "car-used"].includes(adViewType);
  const review = isValidAdViewType ? getReview(brand, carModelTaxonomy, year) : null;

  useEffect(() => {
    const el = cardRef.current;
    if (!el || !review) return;

    const { title, reviewUrl } = review;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackElementView({
            name: "View expert-review",
            objectId: "MO_pv31",
            elementType: ElementType.Link,
            intent: Intent.View,
            target: {
              type: "Article",
              name: title,
              id: reviewUrl,
            },
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [review, trackElementView]);

  if (!isValidAdViewType || !review) {
    return null;
  }

  const { thumbnailUrl, title, reviewUrl } = review;

  const badgeText = i18n.t({
    id: "expertReviewCard.badge.label",
    message: "Expert review",
  });

  const trackingClickEvent = () => {
    // Tracking event: MO_ev102
    trackClick({
      name: "Read expert-review",
      objectId: "MO_ev102",
      elementType: ElementType.Link,
      intent: Intent.View,
      target: {
        type: "Article",
        name: title,
        id: reviewUrl,
      },
    });
  };

  return (
    <div ref={cardRef} className="relative flex flex-row overflow-hidden rounded-8 mt-40">
      <img src={thumbnailUrl} alt={title} className="w-[33%] md:w-[189px] h-auto object-cover shrink-0" />
      <div className="flex flex-col justify-center gap-4 p-16 border-2 s-border rounded-r-8 border-l-0 flex-1">
        <div className="flex items-center gap-8 text-s s-text-subtle">
          <BrandLogo height={18} brandOverride={brand === "dba" ? "bilbasen" : brand} />
          {badgeText}
        </div>
        <h2 className="t4 m-0 line-clamp-2">{title}</h2>
        <a
          href={reviewUrl}
          target="_blank"
          onClick={trackingClickEvent}
          className="s-text-link flex items-center gap-4"
          rel="noopener"
        >
          {i18n.t({
            id: "expertReviewCard.link.label.car",
            message: "Read our car review",
            comment: "Link text to read the expert review of the car",
          })}{" "}
          <WIcon name="LinkExternal" />
        </a>
      </div>
    </div>
  );
};
